import dayjs, { Dayjs } from 'dayjs'
import {
  getTimelineRecordForDate,
  IClockinResponse,
  IRemunerationTimelineRecord,
  IRemunerationTimelineRecordStaffRole,
  IStaffMemberDetail,
  Nullable,
  RemunerationTimelineRecord,
  RemunerationTimelineRecordStaffRole,
  transformClockinDetailOnGet,
} from '$models'
import { logger, nullIfBlank, transformStringToNumberProperty } from '$utils'
import {
  Region,
  PayrollSystem,
  Country,
  RemunerationType,
  EmploymentType,
  DEFAULT_GUID,
} from '$constants'
import { getDateStringWithoutTimeZone } from 'src/components/common/helpers'
import {
  StaffMemberNz,
  transformStaffMemberNzOnGet,
  transformStaffMemberNzOnSave,
} from './staffMemberNz'
import {
  StaffMemberAu,
  transformStaffMemberAuOnGet,
  transformStaffMemberAuOnSave,
} from './staffMemberAu'
import {
  StaffMemberXeroAu,
  transformStaffMemberXeroAuOnGet,
  transformStaffMemberXeroAuOnSave,
} from './staffMemberXeroAu'
import { Contact } from '../contact'
import { OnboardingDetails } from '../onboardingDetails'
import useAppStore from '$appStore'

export type StaffMember = StaffMemberNz | StaffMemberAu | StaffMemberXeroAu

export interface BaseStaffMember {
  discriminator?: Country
  id?: string
  posIdentifier?: string
  posStaffName?: string
  datestampStarted: Nullable<Dayjs>
  datestampModified?: Dayjs
  datestampDeleted?: Dayjs
  firstName: Nullable<string>
  lastName: Nullable<string>
  preferredName?: string
  middleName?: string
  dob: Nullable<Dayjs>
  gender?: string
  phoneHome?: string
  phoneMobile?: string
  email: Nullable<string>
  homeAddressUnit?: string
  homeAddressStreet?: string
  homeAddressSuburb?: string
  homeAddressCity?: string
  homeAddressState?: Region
  homeAddressZipCode?: string
  homeAddressCountry?: string
  payCode?: string
  showInRoster: boolean
  lastClock?: IClockinResponse
  thirdPartyEmployeeId?: string | null
  /**
   * Venue that owns the third party employee link, set whether the staff member
   * is linked or not. For staff paid by a single payroll company this is their
   * payroll venue, which can differ from the active venue.
   */
  thirdPartyEmployeeVenueId?: string | null
  /**
   * Pay calendar, read from the venue above. It belongs to one payroll account, so it
   * changes when the user switches to a venue with its own.
   */
  calendarId?: Nullable<string>
  reportsTo?: string
  lineManager?: Contact | null
  applyAdjustments: boolean
  bankAccountName?: string
  bankAccountNumber?: Nullable<string>
  bsb?: Nullable<string>
  emergencyContact?: Nullable<Contact>
  onboardingDetails?: OnboardingDetails

  memberRoles: IRemunerationTimelineRecordStaffRole[]
  remunerationType?: Nullable<RemunerationType>
  employmentType?: Nullable<EmploymentType>
  weeklyContractedHours?: number
  defaultRoleId?: Nullable<string>
  salaryRate?: Nullable<number>
  remunerationTimeline: IRemunerationTimelineRecord[]
}

export const newStaffMember: StaffMember = {
  showInRoster: true,
  datestampStarted: dayjs().startOf('day'),
  remunerationTimeline: [
    {
      isActive: true,
      type: RemunerationType.HourlyRate,
      validFrom: '0001-01-01',
      memberRoles: [],
      venueAssignments: [],
    } as IRemunerationTimelineRecord,
  ],
  firstName: null,
  lastName: null,
  email: null,
  dob: null,
  applyAdjustments: true,
  memberRoles: [],
}

export function mapStaffMemberToStaffMemberDetail(
  staffMember: StaffMember
): IStaffMemberDetail | null {
  try {
    const defaultMemberRole: IRemunerationTimelineRecordStaffRole | undefined =
      staffMember.memberRoles.find(
        (memberRole) => memberRole.roleId === staffMember.defaultRoleId
      )

    return {
      id: staffMember.id,
      datestampDeleted: staffMember.datestampDeleted,
      name: getFullName(staffMember.firstName!, staffMember.lastName),
      posStaffName: staffMember.posStaffName,
      remunerationType: staffMember.remunerationType ?? undefined,
      defaultMemberRoleRoleId: defaultMemberRole?.roleId,
      defaultMemberRoleRoleName: defaultMemberRole?.roleName,
      defaultMemberRoleHourlyRate: defaultMemberRole?.hourlyRate,
      remunerationTimeline:
        staffMember.remunerationTimeline as RemunerationTimelineRecord[],
      memberRoles: [
        ...staffMember.memberRoles,
      ] as RemunerationTimelineRecordStaffRole[],
      lastClockClockinTime: staffMember.lastClock?.clockinTime,
      lastClockClockoutTime: staffMember.lastClock?.clockoutTime ?? undefined,
      lastClockId: staffMember.lastClock?.id,
      lastClockRoleName: staffMember.lastClock?.roleName,
      showInRoster: staffMember.showInRoster,
    }
  } catch (error) {
    logger.error(
      'Failed to map StaffMember to StaffMemberDetail',
      error,
      staffMember
    )
    return null
  }
}

// Function to apply transformations to StaffMember object on get
export const transformBaseStaffMemberOnGet = (
  staffMember: StaffMember
): StaffMember => {
  return {
    ...staffMember,
    dob: staffMember.dob != null ? dayjs.utc(staffMember.dob) : null,
    datestampStarted:
      staffMember.datestampStarted != null
        ? dayjs(staffMember.datestampStarted)
        : null,
    lastClock: staffMember.lastClock
      ? transformClockinDetailOnGet(staffMember.lastClock)
      : undefined,
  }
}

/**
 * The API rejects empty strings, so blank fields go as null - and so does a contact with nothing
 * left in it.
 */
const transformEmergencyContactOnSave = (
  staffMember: StaffMember
): Nullable<Contact> => {
  const { emergencyContact } = staffMember

  if (!emergencyContact) {
    return null
  }

  const firstName = nullIfBlank(emergencyContact.firstName)
  const lastName = nullIfBlank(emergencyContact.lastName)
  const phoneMobile = nullIfBlank(emergencyContact.phoneMobile)
  const relationship = emergencyContact.relationship || null

  if (!firstName && !lastName && !phoneMobile && !relationship) {
    return null
  }

  return {
    ...emergencyContact,
    firstName,
    lastName,
    phoneMobile,
    relationship,
  }
}

// Function to apply transformations to StaffMember object on save
export const transformBaseStaffMemberOnSave = (
  staffMember: StaffMember
): object => {
  return {
    ...staffMember,
    bankAccountNumber: staffMember.bankAccountNumber?.replaceAll('-', ''),
    dob:
      staffMember.dob != null
        ? getDateStringWithoutTimeZone(staffMember.dob)
        : staffMember.dob,
    emergencyContact: transformEmergencyContactOnSave(staffMember),
    salaryRate: staffMember.salaryRate
      ? transformStringToNumberProperty(`${staffMember.salaryRate}`)
      : 0,
    // If the lineManagerId is an empty GUID, it means that the staff member does not
    // have a line manager and the owner of the company is being set as the line manager
    // so we can use its email to show who are we going to send emails to. In this case,
    // we don't want to save back to the database the empty GUID, but null instead
    lineManager:
      staffMember.lineManager?.id === DEFAULT_GUID ||
      staffMember.lineManager?.id === ''
        ? null
        : staffMember.lineManager,
  }
}

// Function to apply transformations to StaffMember object on get
export const transformStaffMemberOnGet = (
  staffMember: StaffMember,
  payrollSystem?: PayrollSystem | null,
  country?: Country
): StaffMember => {
  switch (country) {
    case Country.NewZealand:
      return transformStaffMemberNzOnGet(staffMember)
    case Country.Australia:
      if (payrollSystem === PayrollSystem.XeroAu) {
        return transformStaffMemberXeroAuOnGet(staffMember)
      } else {
        return transformStaffMemberAuOnGet(staffMember)
      }
    default:
      return transformBaseStaffMemberOnGet(staffMember)
  }
}

// Function to apply transformations to StaffMember object on get
export const transformStaffMemberOnSave = (
  payrollSystem: PayrollSystem | null,
  staffMember: StaffMember,
  country: Country
): object => {
  let resultStaffMember: object

  switch (country) {
    case Country.NewZealand:
      resultStaffMember = transformStaffMemberNzOnSave(staffMember)
      break
    case Country.Australia:
      if (payrollSystem === PayrollSystem.XeroAu) {
        resultStaffMember = transformStaffMemberXeroAuOnSave(staffMember)
      } else {
        resultStaffMember = transformStaffMemberAuOnSave(staffMember)
      }
      break
    default:
      resultStaffMember = transformBaseStaffMemberOnSave(staffMember)
      break
  }
  return resultStaffMember
}

/**
 * Calculates the age based on date of birth, without rounding up.
 * If a reference date is provided, calculates age as of that date; otherwise uses today's date.
 *
 * @param dateOfBirth The staff member's date of birth in format 'YYYY/MM/DD'
 * @param referenceDate Optional date to calculate age at in format 'YYYY/MM/DD'. Defaults to today if not specified.
 * @param includeMonths If true, returns age with months as a decimal fraction. If false, returns whole years only.
 * @returns Age in years. If includeMonths is true, includes months as a decimal fraction.
 */
export function calculateAge(
  dateOfBirth: string,
  referenceDate?: string | undefined,
  includeMonths: boolean = false
): number {
  // Parse the dates
  const dob = dayjs(dateOfBirth, 'YYYY/MM/DD')
  const calcDate = referenceDate ? dayjs(referenceDate, 'YYYY/MM/DD') : dayjs()

  // Calculate the difference in years
  let years = calcDate.year() - dob.year()

  // Check if birthday has occurred this year
  if (
    calcDate.month() < dob.month() ||
    (calcDate.month() === dob.month() && calcDate.date() < dob.date())
  ) {
    years--
  }

  // If not including months, return whole years
  if (!includeMonths) {
    return years
  }

  // Calculate months component
  let months = calcDate.month() - dob.month()

  // Adjust for negative months
  if (months < 0) {
    months += 12
  }

  // Adjust for day differences
  if (calcDate.date() < dob.date()) {
    months--
    // If months becomes negative due to day adjustment, add 12
    if (months < 0) {
      months += 12
    }
  }

  // Calculate final age with decimal component
  const age = years + months / 12

  return age
}

/**
 * Overload for backward compatibility to return integer age only
 *
 * @param dateOfBirth The staff member's date of birth in format 'YYYY/MM/DD'
 * @param referenceDate Optional date to calculate age at in format 'YYYY/MM/DD'. Defaults to today if not specified.
 * @returns Age in whole years.
 */
export function calculateAgeInYears(
  dateOfBirth: string,
  referenceDate?: string | undefined
): number {
  return Math.floor(calculateAge(dateOfBirth, referenceDate, false))
}

export function getFullName(
  firstName: string,
  lastName?: Nullable<string>
): string {
  return `${firstName}${lastName ? ` ${lastName}` : ''}`
}

/**
 * Finds the remuneration record to evaluate payroll venue questions against.
 * When a reference date is given, returns the record valid at that date,
 * otherwise falls back to the record active today.
 * @param remunerationTimeline the staff member's remuneration timeline
 * @param referenceDate optional date to resolve the record for
 * @returns the matching record, or undefined if none found
 */
function getPayrollRemunerationRecord(
  remunerationTimeline: IRemunerationTimelineRecord[] | undefined,
  referenceDate?: Dayjs
): IRemunerationTimelineRecord | undefined {
  if (referenceDate) {
    return getTimelineRecordForDate(remunerationTimeline, referenceDate)
  }
  return remunerationTimeline?.find((rt) => rt.isActive)
}

/**
 * Checks whether the staff member's payroll venue is the current venue.
 * When the staff is paid by each company, every assigned venue counts as
 * a payroll venue in its own context.
 * @param remunerationTimeline the staff member's remuneration timeline
 * @param referenceDate optional date to evaluate at, defaults to today
 * @returns true if the staff member's payroll venue is the current venue
 */
export function currentVenueIsPayrollVenue(
  remunerationTimeline: IRemunerationTimelineRecord[] | undefined,
  referenceDate?: Dayjs
): boolean {
  const remuneration = getPayrollRemunerationRecord(
    remunerationTimeline,
    referenceDate
  )
  if (!remuneration) return false

  const activeCompanyId = useAppStore.getState().activeCompany?.id
  if (remuneration.isPaidByEachVenue) {
    return Boolean(
      remuneration.venueAssignments?.some(
        (assignment) => assignment.venueId === activeCompanyId
      )
    )
  }
  return remuneration.payrollVenueId === activeCompanyId
}

/**
 * Whether the payroll account changes with the venue you are in: either every venue pays
 * them from its own, or they are paid by a venue other than the active one.
 * @param remunerationTimeline the staff member's remuneration timeline
 * @param referenceDate optional date to evaluate at, defaults to today
 * @returns true when the account is not simply the active venue's own
 */
export function payrollAccountVariesByVenue(
  remunerationTimeline: IRemunerationTimelineRecord[] | undefined,
  referenceDate?: Dayjs
): boolean {
  const remuneration = getPayrollRemunerationRecord(
    remunerationTimeline,
    referenceDate
  )
  if (!remuneration) return false

  if (remuneration.isPaidByEachVenue) return true

  const activeCompanyId = useAppStore.getState().activeCompany?.id
  return remuneration.payrollVenueId !== activeCompanyId
}

/**
 * Gets the payroll venue short name for a staff member.
 * When the staff is paid by each company, returns the active company's short name
 * (every venue counts as its own payroll venue in that case).
 * @param remunerationTimeline the staff member's remuneration timeline
 * @param referenceDate optional date to evaluate at, defaults to today
 * @returns the payroll venue name or null if not found
 */
export function getPayrollVenueShortName(
  remunerationTimeline: IRemunerationTimelineRecord[] | undefined,
  referenceDate?: Dayjs
): string | null {
  const remuneration = getPayrollRemunerationRecord(
    remunerationTimeline,
    referenceDate
  )
  if (!remuneration) return null

  if (remuneration.isPaidByEachVenue) {
    const activeCompanyId = useAppStore.getState().activeCompany?.id
    return (
      remuneration.venueAssignments?.find(
        (assignment) => assignment.venueId === activeCompanyId
      )?.venueShortName || null
    )
  }

  const payrollVenueId = remuneration.payrollVenueId
  return (
    remuneration.venueAssignments?.find(
      (assignment) => assignment.venueId === payrollVenueId
    )?.venueShortName || null
  )
}
